import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Storage as PostgresIcon,
  Inbox as SqsIcon,
  ViewList as DdbIcon,
  Folder as S3Icon,
  Psychology as LlmIcon,
  Extension as GenericIcon,
} from "@mui/icons-material";

import {
  getServiceCatalog,
  getServiceCatalogByPath,
  bindKnService,
  provisionAndBindKnService,
} from "../../helpers/utils";

const KIND_META = {
  postgres: { label: "Postgres", Icon: PostgresIcon, tint: "#38bdf8" },
  awssqs: { label: "SQS", Icon: SqsIcon, tint: "#f472b6" },
  awsdynamodb: { label: "DynamoDB", Icon: DdbIcon, tint: "#a78bfa" },
  awss3bucket: { label: "S3 Bucket", Icon: S3Icon, tint: "#fbbf24" },
  "llm-gateway": { label: "LLM Gateway", Icon: LlmIcon, tint: "#34d399" },
};
const kindMeta = (p) =>
  KIND_META[p] || { label: p || "Service", Icon: GenericIcon, tint: "#94a3b8" };

/**
 * Two-step bind dialog:
 *   Step 1: pick a Daari kind (radio-like autocomplete).
 *   Step 2: pick an instance of that kind (Autocomplete over
 *           GET /k8s-service-catalog/instances/{path}).
 *
 * Confirm posts to POST /api/kn-service/{app}/bindings?restart=true
 * with body { name: <binding-secret-name> } — the same coord primitive
 * `c2a llm bind` / `c2a daari secrets bind` funnel through.
 *
 * restart=true because we're binding to an already-deployed ksvc;
 * restart=false is only correct for the pre-first-build template path
 * (see wizard-autobind lane in the service-binding audit).
 */
export default function BindDialog({
  open,
  onClose,
  appName,
  existingBindings = [],
  onSuccess,
  onError,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // mode = "existing" (pick an instance) or "create" (provision + bind).
  // Create mode is backed by coord PR #230's provisionAndBind orchestrator.
  const [mode, setMode] = useState("existing");
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedKind, setSelectedKind] = useState(null);
  const [instances, setInstances] = useState([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [newServiceName, setNewServiceName] = useState("");
  const [restart, setRestart] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hideBound, setHideBound] = useState(true);

  // Reset on open/close so a re-open starts fresh.
  useEffect(() => {
    if (!open) {
      setMode("existing");
      setSelectedKind(null);
      setSelectedInstance(null);
      setInstances([]);
      setNewServiceName("");
      setSubmitting(false);
      return;
    }
    setCatalogLoading(true);
    getServiceCatalog(null, (result, err) => {
      setCatalogLoading(false);
      if (err) {
        onError?.(err);
        return;
      }
      // Daari catalog only — the wizard's non-Daari secrets aren't
      // bindable via this dialog.
      setCatalog(Array.isArray(result) ? result : []);
    });
  }, [open, onError]);

  // Fetch instances of the selected kind.
  useEffect(() => {
    if (!selectedKind?.path) {
      setInstances([]);
      setSelectedInstance(null);
      return;
    }
    setInstancesLoading(true);
    setSelectedInstance(null);
    getServiceCatalogByPath({ path: selectedKind.path }, (result, err) => {
      setInstancesLoading(false);
      if (err) {
        onError?.(err);
        setInstances([]);
        return;
      }
      setInstances(Array.isArray(result) ? result : []);
    });
  }, [selectedKind, onError]);

  const alreadyBoundKeys = useMemo(() => {
    const s = new Set();
    for (const b of existingBindings || []) {
      if (b?.k8sName) s.add(b.k8sName);
      if (b?.displayName) s.add(b.displayName);
    }
    return s;
  }, [existingBindings]);

  const instanceOptions = useMemo(() => {
    return instances
      .map((rec) => {
        const md = rec?.metadata || {};
        const labels = md.labels || {};
        const displayName =
          labels["serviceCatalog.bindingDisplayName"] ||
          labels["serviceCatalog.targetDisplayName"] ||
          md.displayName ||
          md.name;
        const bindingName =
          labels["serviceCatalog.bindingName"] ||
          (md.name ? `${md.name}-binding` : null);
        return {
          k8sName: md.name,
          displayName,
          bindingName,
        };
      })
      .filter((o) => o.bindingName)
      .filter((o) => !hideBound || (!alreadyBoundKeys.has(o.bindingName) && !alreadyBoundKeys.has(o.displayName)));
  }, [instances, hideBound, alreadyBoundKeys]);

  // K8s name rules — RFC 1123: lowercase alphanumerics + hyphens, must
  // start + end with alphanumeric. Strict here so a bad name fails
  // client-side rather than turning into a coordinator-side 400.
  const isValidServiceName = (n) =>
    typeof n === "string" &&
    n.length > 0 &&
    n.length <= 40 &&
    /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(n);

  const canConfirm = Boolean(
    !submitting &&
      selectedKind?.path &&
      (mode === "existing"
        ? selectedInstance?.bindingName
        : isValidServiceName(newServiceName))
  );

  const handleConfirm = useCallback(() => {
    if (!canConfirm) return;
    setSubmitting(true);
    if (mode === "existing") {
      bindKnService(
        {
          appName,
          bindingName: selectedInstance.bindingName,
          restart,
        },
        (result, err) => {
          setSubmitting(false);
          if (err) {
            onError?.(err);
            return;
          }
          onSuccess?.(selectedInstance.bindingName);
        }
      );
    } else {
      provisionAndBindKnService(
        {
          appName,
          type: selectedKind.path,
          serviceName: newServiceName,
          restart,
        },
        (result, err) => {
          setSubmitting(false);
          if (err) {
            onError?.(err);
            return;
          }
          onSuccess?.(result?.bindingName || newServiceName);
        }
      );
    }
  }, [
    appName,
    canConfirm,
    mode,
    newServiceName,
    onError,
    onSuccess,
    restart,
    selectedInstance,
    selectedKind,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Bind a Daari service
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.25,
            color: isDarkMode ? "#94a3b8" : "#64748b",
            fontSize: "0.75rem",
          }}
        >
          Attach an existing service so its credentials land in this app's
          {" "}
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            C2A_SYSTEM_ENV
          </Box>{" "}
          at runtime.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Tabs
            value={mode}
            onChange={(_e, v) => {
              if (!v) return;
              setMode(v);
              setSelectedInstance(null);
              setNewServiceName("");
            }}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: isDarkMode ? "#1e293b" : "divider",
              minHeight: 34,
              "& .MuiTab-root": {
                minHeight: 34,
                fontSize: "0.8rem",
                fontWeight: 600,
                textTransform: "none",
              },
              "& .MuiTabs-indicator": { backgroundColor: "#F97316" },
            }}
          >
            <Tab value="existing" label="Pick existing" />
            <Tab value="create" label="Create new" />
          </Tabs>

          {/* Step 1 — kind */}
          <Autocomplete
            options={catalog}
            loading={catalogLoading}
            getOptionLabel={(o) => o?.name || ""}
            value={selectedKind}
            onChange={(_e, v) => setSelectedKind(v)}
            isOptionEqualToValue={(a, b) => a?.path === b?.path}
            renderOption={(props, option) => {
              const meta = kindMeta(option?.path);
              const KindIcon = meta.Icon;
              return (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <KindIcon sx={{ color: meta.tint, fontSize: "1.05rem" }} />
                  <Typography variant="body2">{option?.name}</Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Service kind"
                placeholder="Pick a Daari kind"
                size="small"
              />
            )}
          />

          {/* Step 2 — instance (existing mode) or name input (create mode) */}
          {selectedKind && mode === "existing" && (
            <>
              <Autocomplete
                options={instanceOptions}
                loading={instancesLoading}
                getOptionLabel={(o) => o?.displayName || o?.k8sName || ""}
                value={selectedInstance}
                onChange={(_e, v) => setSelectedInstance(v)}
                isOptionEqualToValue={(a, b) => a?.bindingName === b?.bindingName}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.displayName}
                    </Typography>
                    <Chip
                      size="small"
                      label={option.k8sName}
                      sx={{
                        height: 18,
                        fontSize: "0.65rem",
                        fontFamily: "monospace",
                        background: isDarkMode
                          ? "rgba(148,163,184,0.10)"
                          : "rgba(15,23,42,0.05)",
                      }}
                    />
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Instance"
                    placeholder={
                      instancesLoading
                        ? "Loading…"
                        : instanceOptions.length === 0
                        ? hideBound
                          ? "No unbound instances of this kind."
                          : "No instances of this kind yet."
                        : "Pick an instance"
                    }
                    size="small"
                  />
                )}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={hideBound}
                    onChange={(e) => setHideBound(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                    Hide instances already bound to this app
                  </Typography>
                }
              />
            </>
          )}

          {selectedKind && mode === "create" && (
            <>
              <TextField
                label="New service name"
                placeholder="e.g. myapp-pg"
                size="small"
                value={newServiceName}
                onChange={(e) => setNewServiceName(e.target.value.toLowerCase())}
                helperText={
                  newServiceName && !isValidServiceName(newServiceName)
                    ? "Lowercase letters, digits, and hyphens; must start + end with an alphanumeric; ≤ 40 chars."
                    : "Coordinator will prefix per project (e.g. Postgres becomes db-<ts>-<name>). Provisions a NEW billable resource."
                }
                error={Boolean(newServiceName) && !isValidServiceName(newServiceName)}
              />
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? "#94a3b8" : "#64748b",
                  fontSize: "0.72rem",
                }}
              >
                On success, the service is created AND bound to this app in one
                atomic call. If the bind step fails, the service is rolled back.
              </Typography>
            </>
          )}

          {selectedKind && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={restart}
                    onChange={(e) => setRestart(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                    Restart the app immediately (roll a new revision so the
                    binding takes effect)
                  </Typography>
                }
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canConfirm}
          onClick={handleConfirm}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            background: "#F97316",
            "&:hover": { background: "#EA580C" },
          }}
        >
          {submitting
            ? mode === "create"
              ? "Provisioning…"
              : "Binding…"
            : mode === "create"
              ? "Provision + bind"
              : "Bind"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

